import {
  ChevronDown,
  ChevronRight,
  FileText,
  Folder,
  Image as ImageIcon,
  Paperclip,
} from 'lucide-react'
import { useState } from 'react'
import { baumBauen, type Baumknoten } from '../../lib/ablage'
import { cn } from '../../lib/cn'
import type { DokumentEintrag } from '../../lib/queries'

/**
 * **Der Baum der Ablage** ([Z-14](../../../../../docs/phasen/zusatz-14-ablage.md)).
 *
 * Eine Zeile je Dokument: Pfeil (wenn es Kinder hat) · Symbol nach Art · Titel. Aufgeklappt
 * ist anfangs alles – die Ablage ist klein, und wer sie öffnet, will sehen, was da ist. Was
 * jemand zuklappt, bleibt für die Sitzung zu.
 *
 * Kein Ziehen zum Umhängen. Das Verschieben geht über die Ansicht rechts („Verschieben nach
 * …"): bei einem Baum mit einem Dutzend Einträgen ist ein Auswahlfeld schneller als eine
 * Ziehgeste, und es funktioniert per Tastatur (Leitplanke 3).
 */
export function AblageBaum({
  eintraege,
  gewaehlt,
  waehlen,
}: {
  eintraege: readonly DokumentEintrag[]
  gewaehlt: string | null
  waehlen: (id: string) => void
}) {
  const [zu, setZu] = useState<Set<string>>(() => new Set())
  const baum = baumBauen(eintraege)

  const umschalten = (id: string) =>
    setZu((bisher) => {
      const neu = new Set(bisher)
      if (neu.has(id)) neu.delete(id)
      else neu.add(id)
      return neu
    })

  if (baum.length === 0) {
    return (
      <div className="px-3 py-6 text-center text-xs text-muted">
        Noch leer. Lege oben rechts das erste Dokument an.
      </div>
    )
  }

  return (
    <ul role="tree" className="flex flex-col gap-px">
      {baum.map((k) => (
        <Zweig
          key={k.eintrag.id}
          knoten={k}
          gewaehlt={gewaehlt}
          waehlen={waehlen}
          zu={zu}
          umschalten={umschalten}
        />
      ))}
    </ul>
  )
}

function Zweig({
  knoten,
  gewaehlt,
  waehlen,
  zu,
  umschalten,
}: {
  knoten: Baumknoten<DokumentEintrag>
  gewaehlt: string | null
  waehlen: (id: string) => void
  zu: Set<string>
  umschalten: (id: string) => void
}) {
  const { eintrag, kinder, tiefe } = knoten
  const hatKinder = kinder.length > 0
  const zugeklappt = zu.has(eintrag.id)
  const aktiv = gewaehlt === eintrag.id

  return (
    <li role="treeitem" aria-expanded={hatKinder ? !zugeklappt : undefined} aria-selected={aktiv}>
      <div
        className={cn(
          'flex items-center gap-1 rounded-lg pr-2 text-[13px]',
          aktiv ? 'bg-accent-soft font-semibold text-accent-deep' : 'text-ink2 hover:bg-[#f3f2ef]',
        )}
        style={{ paddingLeft: `${4 + tiefe * 14}px` }}
      >
        {hatKinder ? (
          <button
            type="button"
            onClick={() => umschalten(eintrag.id)}
            title={zugeklappt ? 'aufklappen' : 'zuklappen'}
            aria-label={zugeklappt ? `${eintrag.titel} aufklappen` : `${eintrag.titel} zuklappen`}
            className="grid size-5 shrink-0 cursor-pointer place-items-center rounded text-muted hover:text-ink"
          >
            {zugeklappt ? (
              <ChevronRight className="size-3.5" />
            ) : (
              <ChevronDown className="size-3.5" />
            )}
          </button>
        ) : (
          <span className="size-5 shrink-0" />
        )}
        <button
          type="button"
          onClick={() => waehlen(eintrag.id)}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 py-1.5 text-left"
        >
          <ArtSymbol eintrag={eintrag} />
          <span className="truncate" title={eintrag.titel}>
            {eintrag.titel}
          </span>
        </button>
      </div>
      {hatKinder && !zugeklappt && (
        <ul role="group" className="flex flex-col gap-px">
          {kinder.map((k) => (
            <Zweig
              key={k.eintrag.id}
              knoten={k}
              gewaehlt={gewaehlt}
              waehlen={waehlen}
              zu={zu}
              umschalten={umschalten}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

/** Ordner, Text, Bild oder sonstige Datei – am Symbol erkennbar, ohne den Titel zu lesen. */
export function ArtSymbol({
  eintrag,
  className,
}: {
  eintrag: DokumentEintrag
  className?: string
}) {
  const klasse = cn('size-3.5 shrink-0 opacity-75', className)
  if (eintrag.art === 'ordner') return <Folder className={klasse} aria-label="Ordner" />
  if (eintrag.art === 'markdown') return <FileText className={klasse} aria-label="Text" />
  if (eintrag.mimeTyp?.startsWith('image/'))
    return <ImageIcon className={klasse} aria-label="Bild" />
  return <Paperclip className={klasse} aria-label="Datei" />
}
